import React, { useState, useMemo } from 'react';
import { 
    Grid, 
    Typography, 
    Card,
    Box,
    Chip,
    CircularProgress,
    Alert,
    List,
    ListItem,
    ListItemText,
    Switch,
    Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useGetTenanciesQuery, useGetFeatureSwitchesQuery, useUpdateFeatureSwitchesMutation } from '../../store/services/tenancy.service';
import { useSelector } from 'react-redux';
import { buildTenancyTree, flattenVisibleNodes } from './tenancyTree';

// The endpoint pages by tenancy id, so a page has to be big enough to reach the tenancy being
// viewed. TenantTable asks for the same.
const TENANCY_PAGE_SIZE = 5000;

// Styled components for the hierarchy tree
const TreeContainer = styled(Box)(({ theme }) => ({
    height: '100%',
    overflowY: 'auto',
    overflowX: 'auto',
    padding: theme.spacing(2),
    backgroundColor: 'transparent',
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.body2.fontSize,
    '&::-webkit-scrollbar': {
        width: '8px',
        height: '8px'
    },
    '&::-webkit-scrollbar-track': {
        background: theme.palette.action.hover
    },
    '&::-webkit-scrollbar-thumb': {
        background: theme.palette.action.disabled,
        borderRadius: '4px'
    }
}));

const TreeNode = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    minHeight: '32px',
    whiteSpace: 'nowrap',
    '&:hover': {
        backgroundColor: theme.palette.action.hover
    }
}));

const TreeLine = styled('span')(({ theme }) => ({
    color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
    fontFamily: 'Consolas, Monaco, monospace',
    userSelect: 'none',
    fontSize: '16px',
    lineHeight: '1',
    whiteSpace: 'pre'
}));

const TreeArrow = styled('span')(({ theme }) => ({
    cursor: 'pointer',
    marginRight: '6px',
    fontFamily: theme.typography.fontFamily,
    userSelect: 'none',
    fontSize: theme.typography.body2.fontSize,
    minWidth: '16px',
    display: 'inline-block',
    textAlign: 'center',
    color: theme.palette.text.secondary
}));

const FamilyTree = () => {
    const currentTenancyId = useSelector((state) => state.tenancy.tenancyId);
    // Tracking what is collapsed rather than what is expanded means the whole tree is open on
    // first render, with no effect to fire once the tenancies arrive.
    const [collapsed, setCollapsed] = useState(new Set());
    const [selectedId, setSelectedId] = useState(null);

    // Fetch tenancies with hierarchy
    const {
        data: tenanciesData,
        isLoading,
        error
    } = useGetTenanciesQuery({
        perPage: TENANCY_PAGE_SIZE,
        page: 0,
        includes: '',
        searchTerm: '',
        additionalQueryParams: ''
    });
    
    const { rootNode, tenancyMap } = useMemo(
        () => buildTenancyTree(tenanciesData, currentTenancyId),
        [tenanciesData, currentTenancyId]
    );

    const visibleNodes = useMemo(
        () => flattenVisibleNodes(rootNode, collapsed, currentTenancyId),
        [rootNode, collapsed, currentTenancyId]
    );

    // Defaulting to the current tenancy here rather than in an effect means the switches panel
    // is populated on the first render that has data. Falling back the same way covers a
    // selection left over from a tenancy the tree no longer holds.
    const selectedTenancy = tenancyMap.get(selectedId) ?? tenancyMap.get(parseInt(currentTenancyId, 10)) ?? null;

    // Fetch feature switches for selected tenancy
    const { 
        data: featureSwitchesResponse, 
        isLoading: featureSwitchesLoading 
    } = useGetFeatureSwitchesQuery(
        { tenancyId: selectedTenancy?.id },
        { skip: !selectedTenancy?.id } // Skip if no tenancy selected
    );
    
    // Extract the actual feature switches from the response
    const featureSwitches = featureSwitchesResponse?.data || featureSwitchesResponse;
    
    // Update feature switches mutation
    const [updateFeatureSwitches] = useUpdateFeatureSwitchesMutation();
    
    // Handle feature switch toggle
    const handleFeatureSwitchToggle = async (key, currentValue) => {
        if (!selectedTenancy?.id) return;
        
        try {
            await updateFeatureSwitches({
                tenancyId: selectedTenancy.id,
                data: {
                    [key]: !currentValue
                }
            }).unwrap();
        } catch (error) {
            console.error('Failed to update feature switch:', error);
        }
    };

    const handleToggle = (nodeId) => {
        setCollapsed(prev => {
            const newSet = new Set(prev);
            if (newSet.has(nodeId)) {
                newSet.delete(nodeId);
            } else {
                newSet.add(nodeId);
            }
            return newSet;
        });
    };

    const handleNodeClick = (node) => {
        setSelectedId(node.id);
        // Also toggle expand/collapse when clicking the name if it has children
        if (node.hasChildren) {
            handleToggle(node.id);
        }
    };

    const renderTreeNode = (node) => {
        const isExpanded = !collapsed.has(node.id);
        const isSelected = selectedTenancy?.id === node.id;
        
        return (
            <Box key={node.id}>
                <TreeNode 
                    onClick={() => handleNodeClick(node)}
                    sx={{ 
                        cursor: 'pointer',
                        backgroundColor: isSelected ? 'action.selected' : 'transparent',
                        fontWeight: node.isCurrentTenancy ? 'bold' : 'normal'
                    }}
                >
                    {/* Render parent continuation lines */}
                    {node.parentLines.map((showLine, idx) => (
                        <TreeLine key={idx}>
                            {showLine ? '│  ' : '   '}
                        </TreeLine>
                    ))}
                    
                    {/* Render the connector */}
                    {node.level > 0 && (
                        <TreeLine>
                            {node.isLast ? '└─ ' : '├─ '}
                        </TreeLine>
                    )}
                    
                    {/* Render expand/collapse arrow */}
                    {node.hasChildren ? (
                        <TreeArrow>
                            {isExpanded ? '▼' : '▶'}
                        </TreeArrow>
                    ) : (
                        <span style={{ display: 'inline-block', width: '16px' }}></span>
                    )}
                    
                    {/* Render node name with ID */}
                    <Typography 
                        variant="body2" 
                        component="span" 
                        sx={{ 
                            ml: 0.5,
                            color: node.isCurrentTenancy ? 'primary.main' : 'text.primary'
                        }}
                    >
                        [{node.id}] {node.name}
                        {node.isCurrentTenancy && (
                            <Chip 
                                label="Current" 
                                size="small" 
                                color="primary" 
                                sx={{ ml: 1, height: '20px' }}
                            />
                        )}
                    </Typography>
                </TreeNode>
            </Box>
        );
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error">
                Failed to load tenancy hierarchy. Please try again later.
            </Alert>
        );
    }

    return (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <Typography variant="h5" gutterBottom>
                    Tenancy Family Tree
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                    View the hierarchical structure of tenancies in your organization
                </Typography>
            </Grid>

            {/* Split View */}
            <Grid item xs={12}>
                <Card sx={{ height: 'calc(100vh - 400px)', minHeight: '500px', p: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2, height: '100%' }}>
                        {/* Left Panel - Tree View */}
                        <TreeContainer id="family-tree" sx={{
                            flex: '0 0 40%', 
                            minWidth: '300px',
                            maxWidth: '500px'
                        }}>
                            {visibleNodes.length === 0 ? (
                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography variant="body1" color="text.secondary">
                                        Tenancy {currentTenancyId} was not found in the tenancy list
                                    </Typography>
                                </Box>
                            ) : (
                                visibleNodes.map(node => renderTreeNode(node))
                            )}
                        </TreeContainer>

                        {/* Right Panel - Feature Switches */}
                        <Box id="family-tree-switches" sx={{
                            flex: '1 1 60%', 
                            overflow: 'auto',
                            minWidth: 0,
                            p: 2,
                            borderLeft: theme => `1px solid ${theme.palette.divider}`
                        }}>
                            {!selectedTenancy ? (
                                <Box sx={{ 
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: '100%'
                                }}>
                                    <Typography variant="body1" color="text.secondary">
                                        Select a tenancy to view feature switches
                                    </Typography>
                                </Box>
                            ) : featureSwitchesLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                    <CircularProgress size={30} />
                                </Box>
                            ) : (
                                <Box>
                                    <Typography variant="h6" gutterBottom>
                                        Feature Switches for [{selectedTenancy.id}] {selectedTenancy.name}
                                    </Typography>
                                    <Divider sx={{ mb: 2 }} />
                                    {(() => {
                                        if (!featureSwitches || typeof featureSwitches !== 'object') {
                                            return (
                                                <Typography variant="body2" color="text.secondary">
                                                    No feature switches available for this tenancy
                                                </Typography>
                                            );
                                        }
                                        
                                        const switches = Object.entries(featureSwitches)
                                            .filter(([key, value]) => 
                                                // Only show boolean feature switches, filter out metadata
                                                typeof value === 'boolean' && 
                                                !['data', 'message', 'rowsAffected', 'status'].includes(key)
                                            )
                                            .sort(([a], [b]) => a.localeCompare(b));
                                        
                                        if (switches.length === 0) {
                                            return (
                                                <Typography variant="body2" color="text.secondary">
                                                    No feature switches configured for this tenancy
                                                </Typography>
                                            );
                                        }
                                        
                                        return (
                                            <List dense sx={{ pt: 0 }}>
                                                {switches.map(([key, value], index) => (
                                                    <React.Fragment key={key}>
                                                        <ListItem sx={{ pl: 0, pr: 0 }}>
                                                            <Switch 
                                                                checked={value}
                                                                onChange={() => handleFeatureSwitchToggle(key, value)}
                                                                size="small"
                                                                color="primary"
                                                                sx={{ mr: 2 }}
                                                            />
                                                            <ListItemText 
                                                                primary={key}
                                                                primaryTypographyProps={{ 
                                                                    variant: 'body2'
                                                                }}
                                                            />
                                                        </ListItem>
                                                        {index < switches.length - 1 && (
                                                            <Divider component="li" />
                                                        )}
                                                    </React.Fragment>
                                                ))}
                                            </List>
                                        );
                                    })()}
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Card>
            </Grid>
        </Grid>
    );
};

export default FamilyTree;